import { z } from 'zod';

// 02.5-calendar.md paso a′ — schemas para las formas reales de request/response del dominio
// `calendar`, verificadas contra los DTOs y casos de uso del backend
// (`backend/src/calendar/domain/{CalendarDTO,CalendarEventDTO}.js`,
// `backend/src/calendar/application/{list-events,get-event,list-calendars,delete-event,
// update-event,create-event}/*`, `backend/src/customs/application/get-custom/GetCustom.js`),
// no inventadas desde cómo el frontend las consume hoy.
//
// `.parse()` en todos lados (nunca `.safeParse()`), `z.looseObject` en todo — mismo criterio que
// `features/databases/schemas.js`: el backend puede agregar campos (`CalendarEventDTO`'s propio
// comentario dice que `ListEvents` agrega columnas vía JOIN que `GetEvent`/`CreateEvent`/
// `UpdateEvent` no traen), y un allowlist estricto rompería en silencio el día que eso pase.

const nullish = (schema) => schema.nullish();
const id = z.union([z.string(), z.number()]);

// ── Evento (`CalendarEventDTO`'s FIELDS — todos opcionales salvo `id`, `ListEvents` agrega
// task_id/task_stage/task_space_id/recurrent_interval/recurrent_unit/task_assignee/attendees
// vía JOIN que GetEvent/CreateEvent/UpdateEvent no traen) ────────────────────────────────────
export const CalendarEventSchema = z.looseObject({
    id: nullish(id),
    title: nullish(z.string()),
    description: nullish(z.string()),
    start_time: nullish(z.string()),
    end_time: nullish(z.string()),
    all_day: nullish(z.boolean()),
    type: nullish(z.string()),
    calendar_id: nullish(id),
    created_by: nullish(id),
    recurrence: z.unknown().nullish(),
    exceptions: z.unknown().nullish(),
    google_event_id: nullish(z.string()),
    created_at: nullish(z.string()),
    parent_task_id: nullish(id),
    fragment_info: z.unknown().nullish(),
    origin_table_id: nullish(z.string()),
    origin_row_id: nullish(id),
    origin_col_id: nullish(z.string()),
    color: nullish(z.string()),
    task_id: nullish(id),
    task_stage: nullish(z.string()),
    task_space_id: nullish(id),
    recurrent_interval: nullish(z.number()),
    recurrent_unit: nullish(z.string()),
    task_assignee: nullish(z.string()),
    attendees: z.unknown().nullish(),
});

// ── Respuesta de `fetchEvents` (`ListEvents.execute`) ───────────────────────────────────────
export const EventsListResponseSchema = z.looseObject({
    success: z.boolean(),
    events: nullish(z.array(CalendarEventSchema)),
    error: nullish(z.string()),
});

// ── Respuesta de `fetchEventById`/`createEvent`/`updateEvent` (`GetEvent`/`CreateEvent`/
// `UpdateEvent.execute`) — `message` (no `error`) es lo que manda el 403 de
// `UpdateEvent._checkPermission`/`DeleteEvent._checkPermission` (§7 riesgo 16), ambos campos
// opcionales acá a propósito. ────────────────────────────────────────────────────────────────
export const EventResponseSchema = z.looseObject({
    success: z.boolean(),
    event: nullish(CalendarEventSchema),
    error: nullish(z.string()),
    message: nullish(z.string()),
});

// ── Respuesta de `deleteEvent` (`DeleteEvent.execute`) ──────────────────────────────────────
export const DeleteEventResponseSchema = z.looseObject({
    success: z.boolean(),
    error: nullish(z.string()),
    message: nullish(z.string()),
});

// ── Calendario (`CalendarDTO`'s FIELDS) ─────────────────────────────────────────────────────
export const CalendarSchema = z.looseObject({
    id: nullish(id),
    name: nullish(z.string()),
    color: nullish(z.string()),
    user_id: nullish(id),
    is_system: nullish(z.boolean()),
    google_sync_token: z.unknown().nullish(),
    created_at: nullish(z.string()),
    timezone: nullish(z.string()),
});

// ── Respuesta de `fetchCalendars` (`ListCalendars.execute`) ─────────────────────────────────
export const CalendarsResponseSchema = z.looseObject({
    success: z.boolean(),
    calendars: nullish(z.array(CalendarSchema)),
    error: nullish(z.string()),
});

// ── Respuesta de `fetchTimeAreas`/`fetchCalendarView` (`GetCustom.execute` — endpoint genérico
// `/api/customs/:userId/:key`, `value` es lo que sea que se haya guardado bajo esa key: el
// array de áreas de tiempo, o el string de vista preferida). `value` se deja `z.unknown()` acá
// -- las propias `useTimeAreas`/`useCalendarView` (hooks/useCalendarData.js) ya narrowean con
// `Array.isArray(data.value)` / `data.value` truthy antes de usarlo, y forzar un shape acá
// duplicaría esa validación con una fuente de verdad distinta. ─────────────────────────────────
export const CustomValueResponseSchema = z.looseObject({
    success: z.boolean(),
    value: z.unknown().nullish(),
    error: nullish(z.string()),
});

// ── Payload de `createEvent`/`updateEvent` (`POST`/`PUT /api/events`) — deliberadamente NUNCA
// `.strict()` (02.5-calendar.md §4 "a′", instrucción explícita del plan): verificado contra los
// 2 shapes reales y distintos que llegan hoy --
// - `celda_fecha.jsx` (databases, slice 4): `attendees` SIN `id` (lo saca a propósito, ver
//   `02.4-databases.md` §7 riesgo 5) + `origin_table_id`/`origin_row_id`/`origin_col_id`.
// - `ComponentTaskHoy.jsx`/`buildEventPayload` (calendarHelpers.js, esta slice): `attendees` CON
//   `id` (dispara `_notifyAttendees`/`_notifyNewAttendees` en el backend, §3.1).
// Además, `updateEvent` recibe PARCIALES reales hoy (`ComponentCalendar.jsx`: solo
// `{end_time}` en un resize, solo `{exceptions}` al partir una instancia recurrente) -- todos
// los campos opcionales, ninguno requerido. `actorId`/`created_by` no son columnas de
// `CalendarEventDTO` (el backend los extrae/descarta antes de escribir, ver `UpdateEvent.js`
// `delete updates.actorId`) pero sí viajan en el payload real -- deben pasar el parse, no
// rechazarse.
export const EventPayloadSchema = z.looseObject({
    title: nullish(z.string()),
    description: nullish(z.string()),
    start_time: nullish(z.string()),
    end_time: nullish(z.string()),
    all_day: nullish(z.boolean()),
    calendar_id: nullish(id),
    created_by: nullish(id),
    actorId: nullish(id),
    recurrence: z.unknown().nullish(),
    exceptions: z.unknown().nullish(),
    attendees: nullish(z.array(z.looseObject({
        id: nullish(id),
        name: nullish(z.string()),
        email: nullish(z.string()),
        avatar: nullish(z.string()),
        image: nullish(z.string()),
    }))),
    origin_table_id: nullish(z.string()),
    origin_row_id: nullish(id),
    origin_col_id: nullish(z.string()),
});
